/**
 * Generato automaticamente.
 * framework-procedimenti-workflow
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetWorkflowControllerStartProcessInstanceByKey200,
  GetWorkflowControllerStartProcessInstanceByKeyParams,
  ProcessDefinitionDto,
  ProcessDefintionXmlDto,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const getWorkflowControllerStartProcessInstanceByKey = (
  params: GetWorkflowControllerStartProcessInstanceByKeyParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GetWorkflowControllerStartProcessInstanceByKey200>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/startProcessInstanceByKey`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerStartProcessInstanceByKeyQueryKey = (
  params: GetWorkflowControllerStartProcessInstanceByKeyParams,
) =>
  [
    `/framework-procedimenti-workflow/workflow-controller/startProcessInstanceByKey`,
    ...(params ? [params] : []),
  ] as const;

export const getGetWorkflowControllerStartProcessInstanceByKeyQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>
  >,
  TError = ErrorType<unknown>,
>(
  params: GetWorkflowControllerStartProcessInstanceByKeyParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerStartProcessInstanceByKeyQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>>
  > = ({ signal }) =>
    getWorkflowControllerStartProcessInstanceByKey(
      params,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, ...queryOptions };
};

export type GetWorkflowControllerStartProcessInstanceByKeyQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>>
  >;
export type GetWorkflowControllerStartProcessInstanceByKeyQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerStartProcessInstanceByKey = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>
  >,
  TError = ErrorType<unknown>,
>(
  params: GetWorkflowControllerStartProcessInstanceByKeyParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWorkflowControllerStartProcessInstanceByKey>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerStartProcessInstanceByKeyQueryOptions(
      params,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitions = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefinitionDto[]>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definitions`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionsQueryKey = () =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definitions`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitions(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>
>;
export type GetWorkflowControllerProcessDefinitionsQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitions = <
  TData = Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitions>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerProcessDefinitionsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitionsLast = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefinitionDto[]>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definitions/last`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionsLastQueryKey = () =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definitions/last`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionsLastQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>
  >,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionsLastQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitionsLast(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionsLastQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>>
  >;
export type GetWorkflowControllerProcessDefinitionsLastQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitionsLast = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>
  >,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionsLast>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerProcessDefinitionsLastQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitionId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefinitionDto>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definition/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionIdQueryKey = (
  id: string,
) =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definition/${id}`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitionId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>
>;
export type GetWorkflowControllerProcessDefinitionIdQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitionId = <
  TData = Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetWorkflowControllerProcessDefinitionIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitionIdXml = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefintionXmlDto>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definition/${id}/xml`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionIdXmlQueryKey = (
  id: string,
) =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definition/${id}/xml`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionIdXmlQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionIdXmlQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitionIdXml(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionIdXmlQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>>
  >;
export type GetWorkflowControllerProcessDefinitionIdXmlQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitionIdXml = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>
  >,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionIdXml>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerProcessDefinitionIdXmlQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitionLastKeyKey = (
  key: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefinitionDto>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definition/last/key/${key}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionLastKeyKeyQueryKey = (
  key: string,
) =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definition/last/key/${key}`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionLastKeyKeyQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>
  >,
  TError = ErrorType<unknown>,
>(
  key: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionLastKeyKeyQueryKey(key);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitionLastKeyKey(
      key,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!key, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionLastKeyKeyQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>>
  >;
export type GetWorkflowControllerProcessDefinitionLastKeyKeyQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitionLastKeyKey = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>
  >,
  TError = ErrorType<unknown>,
>(
  key: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getWorkflowControllerProcessDefinitionLastKeyKey>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerProcessDefinitionLastKeyKeyQueryOptions(
      key,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getWorkflowControllerProcessDefinitionKeyKey = (
  key: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ProcessDefinitionDto[]>(
    {
      url: `/framework-procedimenti-workflow/workflow-controller/process-definition/key/${key}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetWorkflowControllerProcessDefinitionKeyKeyQueryKey = (
  key: string,
) =>
  [
    `/framework-procedimenti-workflow/workflow-controller/process-definition/key/${key}`,
  ] as const;

export const getGetWorkflowControllerProcessDefinitionKeyKeyQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>
  >,
  TError = ErrorType<unknown>,
>(
  key: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetWorkflowControllerProcessDefinitionKeyKeyQueryKey(key);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>>
  > = ({ signal }) =>
    getWorkflowControllerProcessDefinitionKeyKey(key, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!key, ...queryOptions };
};

export type GetWorkflowControllerProcessDefinitionKeyKeyQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>>
  >;
export type GetWorkflowControllerProcessDefinitionKeyKeyQueryError =
  ErrorType<unknown>;

export const useGetWorkflowControllerProcessDefinitionKeyKey = <
  TData = Awaited<
    ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>
  >,
  TError = ErrorType<unknown>,
>(
  key: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWorkflowControllerProcessDefinitionKeyKey>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetWorkflowControllerProcessDefinitionKeyKeyQueryOptions(key, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
